<template>
  <div class="org-container mr-3" :class="{ resizing }" :style="wrapperStyle">
    <div
      class="org-resize-handle"
      @mousedown="startResize" />
    <div class="org-header">
      <div class="org-toolbar">
        <a-input-search
          class="org-toolbar-search"
          v-model="searchValue"
          :placeholder="$t('iamee.organization_search')"
          allowClear />
        <a-dropdown :trigger="['click']" overlay-class-name="org-toolbar-menu">
          <a-button class="org-toolbar-btn" @click.prevent>
            <icon type="more" />
          </a-button>
          <template #overlay>
            <a-menu>
              <template v-for="(item) in menuItems">
                <a-menu-item :key="item.key" @click="item.action" :disabled="item.disabled">
                  <a-tooltip>
                    <template #title v-if="item.disabled && item.tooltip">{{item.tooltip}}</template>
                    <div class="org-menu-item d-flex align-items-center">
                      <a-icon
                        v-if="item.iconType === 'a-icon'"
                        class="org-menu-icon"
                        :type="item.icon"
                        :spin="item.spin" />
                      <icon v-else class="org-menu-icon" :type="item.icon" />
                      <span class="org-menu-text">{{item.text}}</span>
                    </div>
                  </a-tooltip>
                </a-menu-item>
              </template>
            </a-menu>
          </template>
        </a-dropdown>
      </div>
      <div class="ant-tree">
        <li @click="() => {selectedKeys = ['root']}">
          <div :class="`ant-tree-node-content-wrapper org-title d-flex align-items-center ${isSelectedRoot ? 'ant-tree-node-selected' : 'tree-node-title'}`">
            <a-icon class="tree-node-icon" style="font-weight: bold;margin-left:5px" size="17" type="home" />
            <span class="ml-2 tree-node-title">{{tree.title}}</span>
          </div>
        </li>
      </div>
    </div>
    <div id="org-body" class="org-body">
      <a-tree
        v-bind="treeConfig"
        @select="handleTreeNodeSelect"
        @expand="handleTreeExpand"
        style="padding:0 5px 10px 8px">
        <a-icon slot="switcherIcon" type="caret-down" style="font-size: 16px;color:#999" />
        <icon slot="organization" class="tree-node-icon" style="font-size: 14px;color:#999" type="organization" />
      </a-tree>
    </div>
  </div>
</template>

<script>
import WindowsMixin from '@/mixins/windows'
import {
  buildOrgTree,
  clampOrgSidebarWidth,
  deepFilterOrgTree,
  DEFAULT_ORG_SIDEBAR_WIDTH,
  getStoredOrgSidebarWidth,
  setStoredOrgSidebarWidth,
} from '@/utils/organizationTree'

export default {
  name: 'OrganizationConfig',
  mixins: [WindowsMixin],
  props: {
    organization: Object,
    syncLoading: Boolean,
  },
  data () {
    return {
      loading: false,
      searchValue: '',
      tree: {},
      treeData: [],
      nodeList: [],
      selectedKeys: ['root'],
      expandedKeys: [],
      expandedTempKeys: [],
      sidebarHeight: 0,
      sidebarWidth: DEFAULT_ORG_SIDEBAR_WIDTH,
      resizing: false,
      organizationColumns: [
        {
          field: 'name',
          title: this.$t('table.title.name'),
        },
        {
          field: 'description',
          title: this.$t('table.title.desc'),
        },
      ],
    }
  },
  computed: {
    treeConfig () {
      const config = {
        showIcon: true,
        expandedKeys: this.expandedKeys,
        selectedKeys: this.selectedKeys,
        treeData: this.filteredTreeData,
      }
      return config
    },
    isSelectedRoot () {
      return this.selectedKeys.includes('root')
    },
    wrapperStyle () {
      const style = {
        width: `${this.sidebarWidth}px`,
        flex: `0 0 ${this.sidebarWidth}px`,
      }
      if (this.sidebarHeight) {
        style.height = `${this.sidebarHeight}px`
      }
      return style
    },
    filteredTreeData () {
      if (this.searchValue) {
        return deepFilterOrgTree(this.treeData, this.searchValue)
      }
      return this.treeData
    },
    menuItems () {
      // 全部操作始终展示；新建/修改带当前选中节点，启用/禁用/删除针对主组织
      return [
        {
          key: 'sync',
          iconType: 'a-icon',
          icon: 'sync',
          spin: this.syncLoading,
          text: this.$t('iamee.sync'),
          disabled: this.syncLoading,
          action: this.syncOrgNodes,
        },
        {
          key: 'create',
          iconType: 'a-icon',
          icon: 'plus',
          text: this.$t('common.create'),
          action: this.handleCreateTreeNode,
        },
        {
          key: 'edit',
          iconType: 'a-icon',
          icon: 'edit',
          text: this.$t('table.action.modify'),
          action: this.handleEditTreeNode,
        },
        {
          key: 'enabled',
          iconType: 'a-icon',
          icon: 'check',
          text: this.$t('scope.text_19'),
          disabled: this.organization.enabled,
          action: this.handleEnabledTreeNode,
        },
        {
          key: 'disabled',
          iconType: 'a-icon',
          icon: 'stop',
          text: this.$t('scope.text_20'),
          disabled: !this.organization.enabled,
          action: this.handleDisabledTreeNode,
        },
        {
          key: 'delete',
          iconType: 'icon',
          icon: 'delete',
          text: this.$t('table.action.delete'),
          disabled: this.organization.enabled,
          tooltip: this.organization.enabled ? this.$t('iamee.organization_disabled_first') : '',
          action: this.handleDeleteTreeNode,
        },
      ]
    },
  },
  watch: {
    selectedKeys: {
      handler: function (val) {
        this.$emit('updateCurrentNode', val[0])
      },
      immediate: true,
    },
    'organization.id': {
      handler: function (val) {
        this.initOrgList()
      },
      immediate: true,
    },
    searchValue: function (val) {
      if (val) {
        this.expandedKeys = this.nodeList.map(item => item.key)
      } else {
        this.expandedKeys = [...this.expandedTempKeys]
      }
    },
  },
  created () {
    this.$oM = new this.$Manager('organizations')
    this.sidebarWidth = getStoredOrgSidebarWidth()
  },
  mounted () {
    this.updateSidebarHeight()
    window.addEventListener('resize', this.updateSidebarHeight)
    this.$bus.$on('GlobalTopAlertUpdate', this.updateSidebarHeight)
  },
  beforeDestroy () {
    this.stopResize()
    window.removeEventListener('resize', this.updateSidebarHeight)
    this.$bus.$off('GlobalTopAlertUpdate', this.updateSidebarHeight)
  },
  methods: {
    startResize (e) {
      e.preventDefault()
      e.stopPropagation()
      this.resizing = true
      this._resizeStartX = e.clientX
      this._resizeStartWidth = this.sidebarWidth
      document.addEventListener('mousemove', this.onResize)
      document.addEventListener('mouseup', this.stopResize)
    },
    onResize (e) {
      if (!this.resizing) return
      const next = this._resizeStartWidth + (e.clientX - this._resizeStartX)
      this.sidebarWidth = clampOrgSidebarWidth(next)
    },
    stopResize () {
      if (!this.resizing) {
        document.removeEventListener('mousemove', this.onResize)
        document.removeEventListener('mouseup', this.stopResize)
        return
      }
      this.resizing = false
      setStoredOrgSidebarWidth(this.sidebarWidth)
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
    },
    updateSidebarHeight () {
      const el = this.$el
      if (!el || typeof el.getBoundingClientRect !== 'function') return
      if (getComputedStyle(el).display === 'none') return
      const top = el.getBoundingClientRect().top
      const bottomGap = 16
      this.sidebarHeight = Math.max(Math.floor(window.innerHeight - top - bottomGap), 200)
    },
    async initOrgList () {
      if (!this.organization.id) return
      try {
        this.loading = true
        const res = await new this.$Manager('organization_nodes').list({
          params: {
            org_id: this.organization.id,
          },
        })
        const { data = [] } = res.data
        const tree = this.genTreeData(data)
        this.tree = tree
        this.treeData = tree.children || []
        this.loading = false
        this.$nextTick(() => this.updateSidebarHeight())
      } catch (err) {
        this.loading = false
        throw err
      }
    },
    applyOrgTreeTitles (node) {
      const treeNode = node
      if (treeNode.id !== 'root' && treeNode.key !== 'root') {
        treeNode.title = (
          <span class="tree-node-title"><span class="tag-title-budge">{(treeNode.tagKey || '').replace('org:', '')}:</span>{treeNode.tagValue}</span>
        )
      }
      treeNode.slots = { icon: 'organization' }
      if (treeNode.children && treeNode.children.length) {
        treeNode.children = treeNode.children.map(child => this.applyOrgTreeTitles(child))
      }
      return treeNode
    },
    genTreeData (nodeList) {
      const { tree, flatNodeList } = buildOrgTree({
        organization: this.organization,
        nodeList,
      })
      this.nodeList = flatNodeList
      this.initExpandedKeys(flatNodeList)
      this.$emit('updateNodeList', flatNodeList)
      return this.applyOrgTreeTitles(tree)
    },
    initExpandedKeys (list) {
      if (this.expandedKeys.length) {
        this.expandedKeys = this.expandedKeys.filter(key => {
          return list.some(item => item.key === key)
        })
      }
    },
    handleTreeNodeSelect (selectedKeys) {
      if (selectedKeys.length) {
        this.selectedKeys = selectedKeys
      }
    },
    handleTreeExpand (expandedKeys) {
      this.expandedKeys = expandedKeys
      if (!this.searchValue) {
        this.expandedTempKeys = expandedKeys
      }
    },
    handleCreateTreeNode () {
      const selectNodes = this.nodeList.filter(item => item.key === (this.selectedKeys && this.selectedKeys[0]) || '')
      this.createDialog('TreeNodeCreateDialog', {
        nodeList: this.nodeList,
        treeData: [this.tree],
        parentOrgId: this.selectedKeys[0],
        orgId: this.organization.id,
        organization: this.organization,
        selectedOrgId: selectNodes.length ? selectNodes[0].keyValue : '',
        success: () => {
          this.$emit('refresh')
          this.initOrgList()
        },
      })
    },
    handleEditTreeNode () {
      this.createDialog('OrganizationNodeEditDialog', {
        nodeList: this.nodeList,
        nodeId: this.selectedKeys[0],
        orgId: this.organization.id,
        success: (type) => {
          if (type === 'organizations') {
            this.$emit('refresh')
          }
          this.initOrgList()
        },
      })
    },
    handleSetLevel () {
      this.createDialog('OrganizationSetLevelDialog', {
        organization: this.organization,
        success: () => {
          this.syncOrgNodes()
        },
      })
    },
    handleDeleteTreeNode () {
      this.createDialog('DeleteResDialog', {
        vm: this,
        data: [this.organization],
        columns: this.organizationColumns,
        title: this.$t('table.action.delete'),
        name: this.$t('iamee.organization'),
        ok: async () => {
          await new this.$Manager('organizations').delete({
            id: this.organization.id,
          })
          this.$emit('refresh')
        },
      })
    },
    handleEnabledTreeNode () {
      this.createDialog('EnabledSwitchDialog', {
        vm: this,
        data: [this.organization],
        columns: this.organizationColumns,
        action: 'enable',
        fields: ['name', 'description', 'status'],
        resourceName: this.$t('iamee.organization'),
        onOk: async () => {
          await new this.$Manager('organizations').performAction({
            action: 'enable',
            id: this.organization.id,
          })
          this.$emit('refresh')
        },
      })
    },
    handleDisabledTreeNode () {
      this.createDialog('EnabledSwitchDialog', {
        vm: this,
        data: [this.organization],
        columns: this.organizationColumns,
        action: 'disable',
        fields: ['name', 'description', 'status'],
        resourceName: this.$t('iamee.organization'),
        onOk: async () => {
          await new this.$Manager('organizations').performAction({
            action: 'disable',
            id: this.organization.id,
          })
          this.$emit('refresh')
        },
      })
    },
    async syncOrgNodes () {
      this.$emit('sync')
    },
  },
}
</script>

<style lang="less" scoped>
.org-container {
  border: 1px solid #f1f1f1;
  position: relative;
  background: #f1f1f1;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  &.resizing {
    user-select: none;
  }
}

.org-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 2;
  background: transparent;
}

.org-header {
  flex-shrink: 0;
  min-width: 0;
}

.org-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-bottom: 1px solid #e8e8e8;
}

.org-toolbar-btn {
  flex-shrink: 0;
}

.org-toolbar-search {
  flex: 1;
  min-width: 0;
}

.org-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  max-width: 100%;
}

.org-title {
  padding: 5px 0;
  margin-top: 6px;
  font-size: 1.1em;
}

.org-title-selected {
  background: var(--antd-wave-shadow-color);
}

.tag-title-budge {
  font-size: 12px;
  box-sizing: border-box;
  display: inline-block;
  color: #999;
  margin-right: 5px;
}

.tree-node-title:hover {
  color: var(--antd-wave-shadow-color);
  .tag-title-budge {
    color: var(--antd-wave-shadow-color);
  }
}
</style>

<style lang="less">
.org-toolbar-menu {
  .ant-dropdown-menu-item,
  .ant-dropdown-menu-item-disabled {
    padding: 5px 12px;
    font-size: 13px;
    line-height: 20px;
  }
  .org-menu-item {
    min-height: 20px;
  }
  .org-menu-icon {
    flex: 0 0 14px;
    width: 14px;
    height: 14px;
    font-size: 12px !important;
    line-height: 14px;
    color: rgba(0, 0, 0, 0.65);
  }
  .anticon.org-menu-icon {
    font-size: 12px !important;
  }
  .org-menu-text {
    margin-left: 6px;
  }
  .ant-dropdown-menu-item-disabled .org-menu-icon {
    color: rgba(0, 0, 0, 0.25);
  }
}
</style>
